import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import type { ApiResponse } from "../../../shared/types/api";
import type {
  WasteRateSetting,
  WasteRateSettingRequest,
  ShippingFeeSetting,
  ShippingFeeSettingRequest,
  EventDiscount,
  EventDiscountRequest,
} from "../../../domain/types/pricing";

export class CompanyPricingApiService {
  // ========== 손지율 비용 관리 ==========

  /**
   * 손지율 설정 목록 조회
   */
  async getWasteRates(companyId: number): Promise<ApiResponse<WasteRateSetting[]>> {
    const response = await httpClient.get<ApiResponse<WasteRateSetting[]>>(
      NEW_API_ENDPOINTS.companyPricingController.wasteRate.GET(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "손지율 설정을 가져오지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 손지율 설정 생성
   */
  async createWasteRate(
    companyId: number,
    request: WasteRateSettingRequest,
  ): Promise<ApiResponse<WasteRateSetting>> {
    const response = await httpClient.post<ApiResponse<WasteRateSetting>>(
      NEW_API_ENDPOINTS.companyPricingController.wasteRate.POST(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "손지율 설정을 생성하지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 손지율 설정 수정
   */
  async updateWasteRate(
    companyId: number,
    wasteRateId: number,
    request: WasteRateSettingRequest,
  ): Promise<ApiResponse<WasteRateSetting>> {
    const response = await httpClient.put<ApiResponse<WasteRateSetting>>(
      NEW_API_ENDPOINTS.companyPricingController.wasteRate.PUT(companyId, wasteRateId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "손지율 설정을 수정하지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 손지율 설정 삭제
   */
  async deleteWasteRate(
    companyId: number,
    wasteRateId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyPricingController.wasteRate.DELETE(companyId, wasteRateId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "손지율 설정을 삭제하지 못했습니다.");
    }

    return response.data;
  }

  // ========== 배송비 관리 ==========

  /**
   * 배송비 설정 조회
   */
  async getShippingFee(companyId: number): Promise<ApiResponse<ShippingFeeSetting | null>> {
    const response = await httpClient.get<ApiResponse<ShippingFeeSetting | null>>(
      NEW_API_ENDPOINTS.companyPricingController.shippingFee.GET(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "배송비 설정을 가져오지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 배송비 설정 수정 (없으면 생성)
   */
  async updateShippingFee(
    companyId: number,
    request: ShippingFeeSettingRequest,
  ): Promise<ApiResponse<ShippingFeeSetting>> {
    const response = await httpClient.put<ApiResponse<ShippingFeeSetting>>(
      NEW_API_ENDPOINTS.companyPricingController.shippingFee.PUT(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "배송비 설정을 저장하지 못했습니다.");
    }

    return response.data;
  }

  // ========== 이벤트 할인 관리 ==========

  /**
   * 이벤트 할인 목록 조회
   */
  async getEventDiscounts(companyId: number): Promise<ApiResponse<EventDiscount[]>> {
    const response = await httpClient.get<ApiResponse<EventDiscount[]>>(
      NEW_API_ENDPOINTS.companyPricingController.eventDiscount.GET(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "이벤트 할인 목록을 가져오지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 이벤트 할인 생성
   */
  async createEventDiscount(
    companyId: number,
    request: EventDiscountRequest,
  ): Promise<ApiResponse<EventDiscount>> {
    const response = await httpClient.post<ApiResponse<EventDiscount>>(
      NEW_API_ENDPOINTS.companyPricingController.eventDiscount.POST(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "이벤트 할인을 생성하지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 이벤트 할인 수정
   */
  async updateEventDiscount(
    companyId: number,
    eventDiscountId: number,
    request: EventDiscountRequest,
  ): Promise<ApiResponse<EventDiscount>> {
    const response = await httpClient.put<ApiResponse<EventDiscount>>(
      NEW_API_ENDPOINTS.companyPricingController.eventDiscount.PUT(companyId, eventDiscountId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "이벤트 할인을 수정하지 못했습니다.");
    }

    return response.data;
  }

  /**
   * 이벤트 할인 삭제
   */
  async deleteEventDiscount(
    companyId: number,
    eventDiscountId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyPricingController.eventDiscount.DELETE(companyId, eventDiscountId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "이벤트 할인을 삭제하지 못했습니다.");
    }

    return response.data;
  }
}

export const companyPricingApiService = new CompanyPricingApiService();
